/**
 * Configuration for the shared App Support features (feedback intake,
 * planned-outage announcements, notification bell).
 *
 * These features live in de-hub's `app_support` module and are shared by every
 * WIG front end, so two things have to be told apart per app:
 *
 *   VITE_APP_KEY     which front end this is. Sent on every request; the
 *                    server scopes queues and announcements by it. MUST match
 *                    the key used in de-hub's APP_SUPPORT_ADMIN_ROLES setting
 *                    or nobody will be recognised as an admin.
 *
 *   VITE_ADMIN_ROLE  the Azure AD app role that may triage feedback and write
 *                    announcements IN THIS APP. Each app registration has its
 *                    own role names — wig-broker-deals uses `BD.Admin` — so
 *                    this cannot be hard-coded in a template. Accepts a
 *                    comma-separated list if several roles qualify.
 *
 * Both are read once at module load. The server is the real boundary in every
 * case: `VITE_ADMIN_ROLE` decides what UI to draw, and de-hub decides what is
 * actually allowed.
 */

/**
 * de-hub's module path for App Support. A constant rather than an env var:
 * unlike `VITE_APP_API_BASE`, this module is the SAME one for every app — that
 * is the whole point of it being shared — so making it configurable would only
 * create a way to point one app at the wrong queue.
 */
export const API_BASE = "/app_support";

/** The app key sent to de-hub. Falls back to a clearly-wrong value so a
 *  missing setting shows up immediately rather than silently sharing another
 *  app's queue. */
export const APP_KEY =
  import.meta.env?.VITE_APP_KEY?.trim() || "unconfigured-app";

/** Azure AD roles that may administer App Support in this app. */
export const ADMIN_ROLES = (import.meta.env?.VITE_ADMIN_ROLE || "")
  .split(",")
  .map((r) => r.trim())
  .filter(Boolean);

/** Platform superusers — the same two de-hub honours everywhere. */
export const SUPERUSER_ROLES = ["de", "admin"];

/** True when VITE_APP_KEY was never set. Surfaced in the admin UI, because
 *  the symptom otherwise ("my queue is empty") is baffling. */
export const IS_UNCONFIGURED = APP_KEY === "unconfigured-app";

/**
 * Does this set of roles administer App Support here?
 *
 * Pure so it can be tested without a hook or a provider.
 */
export const hasAdminRole = (roles) => {
  const held = new Set(roles || []);
  if (SUPERUSER_ROLES.some((r) => held.has(r))) return true;
  return ADMIN_ROLES.some((r) => held.has(r));
};

export default { APP_KEY, ADMIN_ROLES, SUPERUSER_ROLES, IS_UNCONFIGURED, hasAdminRole };
